<template>
    <div class="health-panel">
        <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
        <button type="button" class="health-summary" @click="toggleChecks">
            <span class="health-summary-text">
                <span class="health-verdict" :class="healthVerdict">{{ healthVerdictLabel }}</span>
                <span class="health-icons">
                    <span class="health-icon-count">
                        <img src="icons/green-checkmark.png" alt="">
                        {{ healthTally.good }}
                    </span>
                    <span class="health-icon-count">
                        <img src="icons/yellow-exclamation-mark.png" alt="">
                        {{ healthTally.warn }}
                    </span>
                    <span class="health-icon-count">
                        <img src="icons/red-x.png" alt="">
                        {{ healthTally.bad }}
                    </span>
                </span>
            </span>
            <span class="card-chevron" :class="{ 'is-open': checksOpen }"></span>
        </button>
        <p class="health-note">{{ healthNote }}</p>
        <div v-show="checksOpen">
        <b-field>
            <div class="checkmark">
                <img v-if="campaign.active_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('active_status')}}</span>
        </b-field>
        <b-field v-if="campaign.handshake_check === false">
            <div class="checkmark">
                <img src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('requires_handshake')}}</span>
        </b-field>
        <b-field>
            <div class="checkmark">
                <img v-if="campaign.attribution_url_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('tracking_urls')}}</span>
        </b-field>
        <b-field v-if="campaign.attribution_url_check && (campaign.number_of_redirects_check === true || campaign.number_of_redirects_check === false)">
            <div class="checkmark">
                <img v-if="campaign.initial_redirects_check === false" src="icons/red-x.png">
                <img v-else-if="campaign.initial_redirects_check === undefined" src="icons/yellow-exclamation-mark.png">
                <img v-else src="icons/green-checkmark.png">
            </div>
            <div>
                <span class="checkmark">{{$t('tracking_link_redirects_to_app_store')}}</span>
                <div>
                    <div class="long-text" v-if="campaign.initial_redirects_check === false && campaign.redirect_test_url">
                        <b>{{$t('test_url')}}:</b> {{campaign.redirect_test_url}}
                    </div>
                    <div class="long-text" v-if="campaign.initial_redirects_check === false && campaign.redirect_test_final_destination">
                        <b>{{$t('destination_url')}}:</b> {{campaign.redirect_test_final_destination}}
                    </div>
                    <div class="long-text" v-if="campaign.initial_redirects_check === false && campaign.redirect_test_final_destination && campaign.app_match === false">
                        <b>{{$t('expected_app')}}:</b> {{campaign.app_identifier}}
                    </div>
                </div>

                <div v-if="redirect_test_result === true" class="top-spacer-10"><b>{{$t('redirect_test_success')}}</b></div>
                <div v-else-if="redirect_test_result === false" class="red top-spacer-10">{{$t('redirect_test_failure')}}</div>
                <div v-if="campaign.status !== 'active' && campaign.initial_redirects_check === false && redirect_test_result === null" class="top-spacer-10"><b>{{$t('campaign_must_be_active')}}</b></div>
                <b-button v-else-if="campaign.initial_redirects_check === false && redirect_test_result === null" v-on:click="redirectTestCampaign" class="top-spacer-10">{{$t('retest')}}</b-button>
            </div>
        </b-field>
        <b-field v-if="campaign.attribution_url_check && (campaign.number_of_redirects_check === true || campaign.number_of_redirects_check === false)">
            <div class="checkmark">
                <img v-if="campaign.number_of_redirects_check === false" src="icons/red-x.png">
                <img v-else src="icons/green-checkmark.png">
            </div>
            <div>
                <span class="checkmark">{{$t('number_of_redirects')}}</span>
                <div class="wrap-text" v-if="campaign.number_of_redirects_check === false">{{$t('number_of_redirects_help_text')}}</div>
            </div>
        </b-field>
        <b-field>
            <div class="checkmark">
                <img v-if="campaign.location_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_targeted_countries')}}</span>
        </b-field>
        <b-field>
            <div class="checkmark">
                <img v-if="campaign.campaign_creative_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_compatible_creatives')}}</span>
        </b-field>
        <b-field v-if="campaign.campaign_creative_check">
            <div class="checkmark">
                <img v-if="campaign.top_banner_sizes_check" src="icons/green-checkmark.png">
                <img v-else src="icons/yellow-exclamation-mark.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_top_creative_sizes')}}</span>
        </b-field>
        <b-field v-if="campaign.campaign_creative_check">
            <div class="checkmark">
                <img v-if="campaign.video_creative_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_video_creatives')}}</span>
        </b-field>
        <b-field v-if="campaign.campaign_creative_check && campaign.playable_creative_check !== undefined">
            <div class="checkmark">
                <img v-if="campaign.playable_creative_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_playable_creatives')}}</span>
        </b-field>
        <b-field v-if="campaign.strategy_check">
            <div class="checkmark">
                <img v-if="campaign.http_redirects_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('tracking_url_failure_rate')}}</span>
        </b-field>
        <div v-if="campaign.strategy_check">
            <b-field style="margin-bottom: 0.75rem">
                <div class="checkmark">
                    <img v-if="campaign.recent_conversions_check" src="icons/green-checkmark.png">
                    <img v-else src="icons/red-x.png">
                </div>
                <span class="checkmark">{{$t('recent_conversions')}}</span>
            </b-field>
            <div v-if="isSuperUser || partnerLevel === 'gold' || partnerLevel === 'silver'">
                <div v-if="reset_checks_result === true"><b>{{$t('reset_checks_success')}}</b></div>
                <div v-else-if="reset_checks_result === false" class="red">{{$t('reset_checks_failure')}}</div>
                <div v-if="campaign.status !== 'active' && campaign.recent_conversions_check === false && reset_checks_result === null"><b>{{$t('campaign_must_be_active')}}</b></div>
                <b-button v-else-if="campaign.recent_conversions_check === false && reset_checks_result === null" v-on:click="resetCampaignChecks">{{$t('reset_checks')}}</b-button>
            </div>
        </div>
        <b-field v-if="campaign.strategy_check && campaign.total_budget > 0">
            <div class="checkmark">
                <img v-if="campaign.total_budget75_percent_check === false" src="icons/yellow-exclamation-mark.png">
                <img v-else-if="campaign.total_budget_check" src="icons/green-checkmark.png">
                <img v-else-if="campaign.total_budget_check === false" src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_budget')}}</span>
        </b-field>
        <b-field v-if="campaign.strategy_check">
            <div class="checkmark">
                <img v-if="campaign.daily_install_cap_check" src="icons/green-checkmark.png">
                <img v-else src="icons/yellow-exclamation-mark.png">
            </div>
            <span class="checkmark">{{$t('below_target_daily_install_cap')}}</span>
        </b-field>
        <b-field v-if="campaign.installs_ltd > 0 && campaign.post_install_events.length > 0 && campaign.post_install_events[0].event_id">
            <div class="checkmark">
                <img v-if="campaign.pie_check" src="icons/green-checkmark.png">
                <img v-else-if="!campaign.installs_ltd || campaign.installs_ltd < 100" src="icons/yellow-exclamation-mark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_post_install_events')}}</span>
        </b-field>
        <b-field v-if="campaign.post_install_events.length > 0 && campaign.post_install_events[0].event_id && campaign.total_budget > 0">
            <div class="checkmark">
                <img v-if="campaign.event_budget_check" src="icons/green-checkmark.png">
                <img v-else src="icons/red-x.png">
            </div>
            <span class="checkmark">{{$t('campaign_has_event_budget')}}</span>
        </b-field>
        <b-field>
          <div class="checkmark">
            <img v-if="flightDateStatus === 'green'" src="icons/green-checkmark.png">
            <img v-else-if="flightDateStatus === 'yellow'" src="icons/yellow-exclamation-mark.png">
            <img v-else src="icons/red-x.png">
          </div>
          <span class="checkmark">{{$t('campaign_flight_date')}}</span>
        </b-field>
        <div v-if="campaign.active_check && campaign.attribution_url_check && campaign.initial_redirects_check && campaign.number_of_redirects_check && campaign.location_check && (campaign.app_creative_check || campaign.campaign_creative_check) && !campaign.strategy_check" class="wrap-text">
            {{$t('campaign_will_start')}}
        </div>
        </div>
    </div>
</template>

<style>
    .health-panel {
        margin-bottom: 8px;
    }
    .health-summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        margin: 0 0 8px;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font-family: inherit;
        cursor: pointer;
        text-align: left;
    }
    .health-summary-text {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
    }
    .health-icons {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
    }
    .health-icon-count {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #0e0c1a;
        font-size: 0.95rem;
        font-weight: 600;
    }
    .health-icon-count img {
        width: 20px;
        height: 16px;
        object-fit: contain;
    }
    .health-verdict {
        font-size: 1.15rem;
        font-weight: 600;
        line-height: 1.2;
    }
    .health-verdict.good {
        color: #00b183;
    }
    .health-verdict.warn {
        color: #e29700;
    }
    .health-verdict.bad {
        color: #f94069;
    }
    .health-note {
        margin: 0 0 8px;
        color: #363636;
        font-size: 0.9rem;
    }
    .health-toggle {
        margin-bottom: 10px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #353fff;
        cursor: pointer;
    }
    div.checkmark img {
        min-width: 31px;
        max-width: 31px;
        min-height: 25px;
        max-height: 25px;
        margin-right: 5px;
    }
    span.checkmark {
        padding-top: 3px;
    }
    div.long-text {
        height: 100%;
        width: 100%;
        margin-top: 5px;
        word-break: break-all;
    }
    div.wrap-text {
        height: 100%;
        width: 100%;
        margin-top: 5px;
        word-break: break-word;
    }
    .red {
        color: #f40000;
    }
    .top-spacer-10 {
        margin-top: 10px;
    }
</style>

<script>
    import session from "@/session";

    const model = {
        isLoading: false,
        isSuperUser: false,
        partnerLevel: 'standard',
        redirect_test_result: null,
        reset_checks_result: null,
        checksExpanded: false
    }

    export default {
        props: ['campaign', 'isSuperUser', 'partnerLevel'],

        data() {
            return model;
        },

        computed: {
            checksOpen() {
                return this.checksExpanded === true;
            },
            healthVerdict() {
                if (this.healthIssues.some(function (issue) { return issue.level === 'bad'; })) {
                    return 'bad';
                }
                if (this.healthIssues.some(function (issue) { return issue.level === 'warn'; })) {
                    return 'warn';
                }
                return 'good';
            },
            healthVerdictLabel() {
                if (this.healthVerdict === 'bad') {
                    return this.$t('health_needs_attention');
                }
                if (this.healthVerdict === 'warn') {
                    return this.$t('health_review');
                }
                return this.$t('health_good');
            },
            healthNote() {
                if (!this.healthIssues.length) {
                    return this.$t('health_ok');
                }
                const shown = this.healthIssues.slice(0, 2).map(function (issue) {
                    return issue.label;
                });
                let note = shown.join(' · ');
                const extra = this.healthIssues.length - shown.length;
                if (extra > 0) {
                    note += ' · +' + extra;
                }
                return note;
            },
            healthTally() {
                const campaign = this.campaign || {};
                let good = 0;
                let warn = 0;
                let bad = 0;
                const add = function (level) {
                    if (level === 'good') {
                        good++;
                    } else if (level === 'warn') {
                        warn++;
                    } else {
                        bad++;
                    }
                };
                add(campaign.active_check ? 'good' : 'bad');
                if (campaign.handshake_check === false) {
                    add('bad');
                }
                add(campaign.attribution_url_check ? 'good' : 'bad');
                const redirectsKnown = campaign.number_of_redirects_check === true || campaign.number_of_redirects_check === false;
                if (campaign.attribution_url_check && redirectsKnown) {
                    if (campaign.initial_redirects_check === false) {
                        add('bad');
                    } else if (campaign.initial_redirects_check == null) {
                        add('warn');
                    } else {
                        add('good');
                    }
                    add(campaign.number_of_redirects_check === false ? 'bad' : 'good');
                }
                add(campaign.location_check ? 'good' : 'bad');
                add(campaign.campaign_creative_check ? 'good' : 'bad');
                if (campaign.campaign_creative_check) {
                    add(campaign.top_banner_sizes_check ? 'good' : 'warn');
                    add(campaign.video_creative_check ? 'good' : 'bad');
                    if (campaign.playable_creative_check !== undefined) {
                        add(campaign.playable_creative_check ? 'good' : 'bad');
                    }
                }
                if (campaign.strategy_check) {
                    add(campaign.http_redirects_check ? 'good' : 'bad');
                    add(campaign.recent_conversions_check ? 'good' : 'bad');
                    if (campaign.total_budget > 0) {
                        if (campaign.total_budget75_percent_check === false) {
                            add('warn');
                        } else if (campaign.total_budget_check) {
                            add('good');
                        } else if (campaign.total_budget_check === false) {
                            add('bad');
                        }
                    }
                    add(campaign.daily_install_cap_check ? 'good' : 'warn');
                }
                const events = campaign.post_install_events || [];
                const hasEvent = events.length > 0 && events[0].event_id;
                if (campaign.installs_ltd > 0 && hasEvent) {
                    if (campaign.pie_check) {
                        add('good');
                    } else if (!campaign.installs_ltd || campaign.installs_ltd < 100) {
                        add('warn');
                    } else {
                        add('bad');
                    }
                }
                if (hasEvent && campaign.total_budget > 0) {
                    add(campaign.event_budget_check ? 'good' : 'bad');
                }
                if (this.flightDateStatus === 'green') {
                    add('good');
                } else if (this.flightDateStatus === 'yellow') {
                    add('warn');
                } else {
                    add('bad');
                }
                return {good: good, warn: warn, bad: bad};
            },
            healthIssues() {
                const campaign = this.campaign || {};
                const issues = [];
                const push = function (level, label) {
                    issues.push({level: level, label: label});
                };
                if (!campaign.active_check) {
                    push('bad', this.$t('active_status'));
                }
                if (campaign.handshake_check === false) {
                    push('bad', this.$t('requires_handshake'));
                }
                if (!campaign.attribution_url_check) {
                    push('bad', this.$t('tracking_urls'));
                }
                const redirectsKnown = campaign.number_of_redirects_check === true || campaign.number_of_redirects_check === false;
                if (campaign.attribution_url_check && redirectsKnown) {
                    if (campaign.initial_redirects_check === false) {
                        push('bad', this.$t('tracking_link_redirects_to_app_store'));
                    } else if (campaign.initial_redirects_check == null) {
                        push('warn', this.$t('tracking_link_redirects_to_app_store'));
                    }
                    if (campaign.number_of_redirects_check === false) {
                        push('bad', this.$t('number_of_redirects'));
                    }
                }
                if (!campaign.location_check) {
                    push('bad', this.$t('campaign_has_targeted_countries'));
                }
                if (!campaign.campaign_creative_check) {
                    push('bad', this.$t('campaign_has_compatible_creatives'));
                }
                if (campaign.campaign_creative_check && !campaign.top_banner_sizes_check) {
                    push('warn', this.$t('campaign_has_top_creative_sizes'));
                }
                if (campaign.campaign_creative_check && !campaign.video_creative_check) {
                    push('bad', this.$t('campaign_has_video_creatives'));
                }
                if (campaign.campaign_creative_check && campaign.playable_creative_check === false) {
                    push('bad', this.$t('campaign_has_playable_creatives'));
                }
                if (campaign.strategy_check && !campaign.http_redirects_check) {
                    push('bad', this.$t('tracking_url_failure_rate'));
                }
                if (campaign.strategy_check && !campaign.recent_conversions_check) {
                    push('bad', this.$t('recent_conversions'));
                }
                if (campaign.strategy_check && campaign.total_budget > 0) {
                    if (campaign.total_budget_check === false) {
                        push('bad', this.$t('campaign_has_budget'));
                    } else if (campaign.total_budget75_percent_check === false) {
                        push('warn', this.$t('campaign_has_budget'));
                    }
                }
                if (campaign.strategy_check && campaign.daily_install_cap_check === false) {
                    push('warn', this.$t('below_target_daily_install_cap'));
                }
                const events = campaign.post_install_events || [];
                const hasEvent = events.length > 0 && events[0].event_id;
                if (campaign.installs_ltd > 0 && hasEvent && !campaign.pie_check) {
                    push((!campaign.installs_ltd || campaign.installs_ltd < 100) ? 'warn' : 'bad', this.$t('campaign_has_post_install_events'));
                }
                if (hasEvent && campaign.total_budget > 0 && !campaign.event_budget_check) {
                    push('bad', this.$t('campaign_has_event_budget'));
                }
                if (this.flightDateStatus === 'red') {
                    push('bad', this.$t('campaign_flight_date'));
                } else if (this.flightDateStatus === 'yellow') {
                    push('warn', this.$t('campaign_flight_date'));
                }
                return issues;
            },
            flightDateStatus() {
              if (!this.campaign.end_date) {
                return 'green';
              }

              const end = new Date(this.campaign.end_date);
              const now = new Date();

              // Compare whole days only
              end.setHours(0, 0, 0, 0);
              now.setHours(0, 0, 0, 0);

              const daysDiff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

              if (daysDiff < 0) {
                return 'red';
              }
              if (daysDiff <= 7) {
                return 'yellow';
              }
              return 'green';
            }
        },

        methods: {
            toggleChecks: function () {
                model.checksExpanded = !this.checksOpen;
            },
            redirectTestCampaignSuccessHandler: function() {
                model.isLoading = false;
                model.redirect_test_result = true;
                this.campaign.redirect_test_url = null;
                this.campaign.redirect_test_final_destination = null;
                this.campaign.app_match = null;
            },

            redirectTestCampaignErrorHandler: function() {
                model.isLoading = false;
                model.redirect_test_result = false;
            },

            redirectTestCampaign: function() {
                if (this.campaign.offer_id) {
                    model.isLoading = true;
                    model.redirect_test_result = null;
                    session.post('redirecttest/' + this.campaign.offer_id, this.campaign, this.redirectTestCampaignSuccessHandler, this.redirectTestCampaignErrorHandler, [])
                }
            },

            resetCampaignChecksSuccessHandler: function() {
                model.isLoading = false;
                model.reset_checks_result = true;
                this.campaign.recent_conversions_check = true;
            },

            resetCampaignChecksErrorHandler: function() {
                model.isLoading = false;
                model.reset_checks_result = false;
            },

            resetCampaignChecks: function() {
                if (this.campaign.offer_id) {
                    model.isLoading = true;
                    model.reset_checks_result = null;
                    session.post('resetchecks/' + this.campaign.offer_id, this.campaign, this.resetCampaignChecksSuccessHandler, this.resetCampaignChecksErrorHandler, [])
                }
            }
        }
    }
</script>
